import { Button } from "@exopengithub/exokit/v2";
import { FloatingFocusManager, useFloating } from "@floating-ui/react";
import { useId, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useEscapeKey } from "src/common/utils/hooks/useEscapeKey.js";
import { ExopenLogo } from "src/features/sidebar/ExopenLogo.js";

// WHY no close on outside click: skipping completes the experience.
export const ExperienceIntro = ({
	title,
	body,
	startLabel,
	skipLabel,
	onStart,
	onSkip,
}: {
	title: string;
	body: string;
	startLabel: string;
	skipLabel: string;
	onStart: () => void;
	onSkip: () => void;
}) => {
	const { t } = useTranslation();
	const titleId = useId();
	const bodyId = useId();
	const startRef = useRef<HTMLButtonElement>(null);
	const { refs, context } = useFloating({ open: true });

	useEscapeKey(onSkip);

	return createPortal(
		<div className="fixed inset-0 z-[1000] flex items-center justify-center bg-[rgb(16_24_40/0.6)] p-4">
			<FloatingFocusManager context={context} initialFocus={startRef}>
				<div
					aria-describedby={bodyId}
					aria-labelledby={titleId}
					aria-modal
					className="relative flex w-[508px] max-w-full animate-v2-fade-in flex-col items-center gap-3 overflow-hidden rounded-[2px] bg-white px-6 pt-12 pb-10 sm:px-14 text-center shadow-v2-xl motion-reduce:animate-none"
					ref={refs.setFloating}
					role="dialog"
				>
					<div
						aria-hidden
						className="pointer-events-none absolute -top-24 -left-24 size-72 rounded-full bg-v2-green-light blur-2xl"
					/>
					<p className="relative text-gray-900 text-v2-sm">{t("Welcome to")}</p>
					<div className="relative h-10 w-[168px] text-v2-green">
						<ExopenLogo />
					</div>
					<h2
						className="relative mt-2 text-balance font-semibold text-gray-900 text-[22px] leading-[30px]"
						id={titleId}
					>
						{title}
					</h2>
					<p className="relative text-v2-gray-dark text-v2-sm" id={bodyId}>
						{body}
					</p>
					<div className="relative flex gap-2 self-stretch pt-5 sm:justify-end">
						<Button onClick={onSkip} size="md" variant="secondaryGray">
							{skipLabel}
						</Button>
						<Button onClick={onStart} ref={startRef} size="md">
							{startLabel}
						</Button>
					</div>
				</div>
			</FloatingFocusManager>
		</div>,
		document.body,
	);
};
